import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Smartphone, Download, ExternalLink } from 'lucide-react';

// ---- inline styles ----
const S: Record<string, React.CSSProperties> = {
  page: {
    fontFamily: "'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
    background: '#F8FAFC',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    WebkitFontSmoothing: 'antialiased',
  },
  header: {
    height: 68,
    display: 'flex',
    alignItems: 'center',
    borderBottom: '1px solid #E2E8F0',
    background: '#fff',
    padding: '0 28px',
  },
  logoMark: {
    width: 38,
    height: 38,
    background: '#EEF2FF',
    borderRadius: 11,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoInner: {
    width: 24,
    height: 24,
    background: 'linear-gradient(135deg,#6366F1 0%,#8B5CF6 100%)',
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(99,102,241,.35)',
  },
  logoTxt: {
    fontWeight: 800,
    fontSize: 17,
    color: '#0F172A',
    letterSpacing: '-0.025em',
    marginLeft: 10,
  },
  main: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 24px',
  },
  card: {
    background: '#fff',
    borderRadius: 32,
    padding: '56px 48px',
    width: '100%',
    maxWidth: 460,
    boxShadow: '0 4px 16px -4px rgba(15,23,42,.08), 0 1px 2px rgba(15,23,42,.04)',
    textAlign: 'center',
  },
  iconCircle: {
    width: 88,
    height: 88,
    borderRadius: '50%',
    margin: '0 auto 32px',
    background: '#EEF2FF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: 26,
    fontWeight: 900,
    letterSpacing: '-0.035em',
    color: '#0F172A',
    marginBottom: 12,
  },
  desc: {
    fontSize: 15,
    color: '#64748B',
    lineHeight: 1.65,
    marginBottom: 36,
  },
  btnPrimary: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    width: '100%',
    height: 56,
    border: 'none',
    borderRadius: 24,
    background: 'linear-gradient(135deg,#6366F1 0%,#8B5CF6 100%)',
    color: '#fff',
    fontWeight: 700,
    fontSize: 16,
    cursor: 'pointer',
    boxShadow: '0 10px 20px rgba(99,102,241,.3)',
    textDecoration: 'none',
    marginBottom: 12,
    transition: 'opacity .15s, transform .12s',
    fontFamily: 'inherit',
  },
  btnGhost: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    width: '100%',
    height: 50,
    border: '1.5px solid #E2E8F0',
    borderRadius: 16,
    background: '#fff',
    color: '#334155',
    fontWeight: 600,
    fontSize: 15,
    cursor: 'pointer',
    textDecoration: 'none',
    marginBottom: 12,
    transition: 'border-color .15s, background .15s',
    fontFamily: 'inherit',
  },
  storeRow: {
    display: 'flex',
    gap: 10,
    marginTop: 8,
  },
  storeBtn: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    height: 44,
    border: '1.5px solid #E2E8F0',
    borderRadius: 12,
    background: '#F8FAFC',
    color: '#334155',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
    textDecoration: 'none',
    transition: 'border-color .15s',
    fontFamily: 'inherit',
  },
  footer: {
    padding: '24px',
    textAlign: 'center',
    fontSize: 12,
    color: '#94A3B8',
  },
  footerLink: {
    color: '#64748B',
    textDecoration: 'none',
    fontWeight: 500,
  },
};

type RouteParams = {
  id?: string;
};

interface Props {
  screen?: string;
}

const OpenInAppPage: React.FC<Props> = ({ screen: screenProp }) => {
  const { id } = useParams<RouteParams>();
  const screen = screenProp;
  const [searchParams] = useSearchParams();
  const [appOpened, setAppOpened] = useState(false);

  // Build the deep link from the current URL path + query
  const buildDeepLink = (): string => {
    const path = screen ?? 'events';
    const base = `getspeakhub://${path}${id ? `/${id}` : ''}`;
    const qs = searchParams.toString();
    return qs ? `${base}?${qs}` : base;
  };

  // Try to open the app automatically on mount
  useEffect(() => {
    const deepLink = buildDeepLink();
    const timer = setTimeout(() => {
      window.location.href = deepLink;
      // After a brief delay, show the manual button in case the app didn't open
      setTimeout(() => setAppOpened(true), 1500);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenApp = () => {
    window.location.href = buildDeepLink();
  };

  return (
    <>
      <style>{`
        @keyframes fadeUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
        .oia-card { animation: fadeUp .65s cubic-bezier(.22,1,.36,1) both; }
        .oia-btn-primary:hover { opacity:.92; transform:translateY(-1px); }
        .oia-btn-ghost:hover { border-color:#CBD5E1 !important; background:#F8FAFC !important; }
        .oia-store-btn:hover { border-color:#CBD5E1 !important; }
      `}</style>

      <div style={S.page}>
        {/* Header */}
        <header style={S.header}>
          <a href="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
            <div style={S.logoMark}>
              <div style={S.logoInner}>
                <span style={{ color: '#fff', fontWeight: 900, fontSize: 13 }}>G</span>
              </div>
            </div>
            <span style={S.logoTxt}>GetSpeakHub</span>
          </a>
        </header>

        {/* Main */}
        <main style={S.main}>
          <div style={S.card} className="oia-card">

            <div style={S.iconCircle}>
              <Smartphone size={40} color="#6366F1" strokeWidth={1.8} />
            </div>

            <h2 style={S.title}>Open in the app</h2>
            <p style={S.desc}>
              {appOpened
                ? "If the app didn't open, tap the button below or download GetSpeakHub from the store."
                : 'Opening the GetSpeakHub app for you…'}
            </p>

            <button
              style={S.btnPrimary}
              className="oia-btn-primary"
              onClick={handleOpenApp}
            >
              <ExternalLink size={18} />
              Open in GetSpeakHub
            </button>

            <p style={{ fontSize: 13, color: '#94A3B8', marginBottom: 12 }}>
              Don't have the app yet?
            </p>

            <div style={S.storeRow}>
              <a
                href="https://apps.apple.com/app/getspeakhub/id000000000"
                target="_blank"
                rel="noreferrer"
                style={S.storeBtn}
                className="oia-store-btn"
              >
                <Download size={15} />
                App Store
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.getspeakhub.app"
                target="_blank"
                rel="noreferrer"
                style={S.storeBtn}
                className="oia-store-btn"
              >
                <Download size={15} />
                Google Play
              </a>
            </div>

          </div>
        </main>

        {/* Footer */}
        <footer style={S.footer}>
          <a href="/terms" style={S.footerLink}>Terms of Service</a>
          {' · '}
          <a href="/privacy" style={S.footerLink}>Privacy Policy</a>
          {' · '}
          © 2026 GetSpeakHub, Inc.
        </footer>
      </div>
    </>
  );
};

export default OpenInAppPage;
